import { Component, OnInit, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import flatpickr from 'flatpickr';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { ConfigService } from '../../services/config.service';
import { UserService, User } from '../../services/user.service';
import { ViewportService } from '../../services/viewport.service';
import { AlertsService } from '../../services/alerts.service';
import { ProductService } from '../../services/product.service';
import { UploadService } from '../services/upload.service';
import { SearchService } from '../../search/services/search.service';
import { FilingService } from '../../records-management/services/filing.service';
import { ExtendedFile } from '../../models/extended-file.interface';
import { WorkflowUtilitiesService } from '../../workflow/services/workflow-utilities.service';
import { BatchService } from '../../workflow/services/batch.service';
import { BrowseService } from '../../browse/services/browse.service';
import { BrowseEditComponent } from '../../browse/components/browse-edit/browse-edit.component';
import { EmailModalComponent } from '../../email/email-modal/email-modal.component';

export interface FormData {
	autoDate: boolean;
	uploadDate: string | null;
	uploadName: string | null;
	keywords: any[] | null;
	description: string | null;
}
export interface UploadInput {
	runOCR: boolean,
	skipDuplicateCheck: boolean,
	generatePDF: boolean,
	extractMetadata: boolean,
	attachMetadata: boolean,
	attachformat: string | null;
	uploadformat: string | null;
	attachRMRecord: boolean;
	rmFields: {
		rm_folder_select: string | null;
		rm_folder_input: string | null;
	}
	skipDuplicateCheckEnabled: boolean;
	library: string;
	detectWorkflow: string;
	addWorkflow: string;
	dxWorkflow: any;
}

export interface Field {
	replacements: any[];
}

@Component({
	selector: 'upload',
	templateUrl: './upload.component.html',
	styleUrls: ['./upload.component.css']
})
export class UploadComponent implements OnInit, OnDestroy {

	@ViewChild('kvForm') kvForm!: NgForm;

	private searchEventSubscription!: Subscription;
	private userServiceSubscription!: Subscription;
	private uploadServiceSubscription!: Subscription;

	LOCALIZATION: any = {};
	permissions: any = {};

	uploadOn: boolean = false;
	uploadFiles: ExtendedFile[] = [];
	uploadSide: boolean = true;
	formData: FormData = {
		autoDate: true,
		uploadDate: "",
		uploadName: "",
		keywords: [{ keyword: "" }],
		description: ""
	};
	ingestMsg: string = "";
	ingestMsgType: string = "info";
	input: UploadInput = {
		runOCR: false,
		skipDuplicateCheck: false,
		generatePDF: false,
		extractMetadata: false,
		attachMetadata: true,
		attachformat: 'template',   // 'template' or 'dynamic';
		uploadformat: 'individual', // 'individual' or 'package';
		attachRMRecord: false,
		rmFields: {
			rm_folder_select: "",
			rm_folder_input: ""
		},
		skipDuplicateCheckEnabled: true,
		library: "",
		detectWorkflow: "DEFAULT",
		addWorkflow: "DEFAULT",
		dxWorkflow: {}
	};
	enabled: any = {};
	libraryDefault: string = "";
	datePickerOptions: any = {};
	workflows: any[] = [];
	rm_folders: any[] = [];
	templateMetadata: { [key: string]: any } = {};
	appendFields: any[] = [];
	dateFormat: string = 'MM/dd/yyyy';
	autoFiller: any = {};
	formFields: any[] = [];
	uploading: boolean = false;
	user: User;
	libraries: any[] = [];
	addWorkflows: string[] = [];
	detectWorkflows: string[] = [];
	fields: Field[] = [];
	private ngZone: NgZone;
	emailNotification: string = '';
	printerNotification: string = '';
	isMobile: boolean = false;

	constructor(
		private uploadService: UploadService,
		private viewportService: ViewportService,
		private userService: UserService,
		private alertsService: AlertsService,
		private productService: ProductService,
		private configService: ConfigService,
		private searchService: SearchService,
		private route: ActivatedRoute,
		private filingService: FilingService,
		private dxUtilsService: WorkflowUtilitiesService,
		private dxBatchService: BatchService,
		private router: Router,
		private renderer: Renderer2,
		private browseService: BrowseService,
		ngZone: NgZone,
		private modalService: NgbModal
	) {
		this.user = this.userService.user;
		this.ngZone = ngZone;

		// Replacement for the viewportsizemonitor directive
		this.viewportService.isMobile$.subscribe(isMobile => {
			this.isMobile = isMobile;
		});
	}

	ngOnInit(): void {
		this.uploadSide = this.configService.getConfig().upload?.uploadDragOnSide !== undefined ? this.configService.getConfig().upload.uploadDragOnSide : true;
		this.input.skipDuplicateCheckEnabled = (this.configService.getConfig().upload && this.configService.getConfig().upload.allowDuplicates) ? this.configService.getConfig().upload.allowDuplicates.enabled : true;
		this.enabled = {
			workflow: this.configService.getConfig().dx && this.configService.getConfig().dx.enabled,
			recMgmt: this.configService.getConfig().recordsManagement && this.configService.getConfig().recordsManagement.enabled
		};
		if (localStorage.getItem("configs")) {
			let servicesConfiguration = JSON.parse(localStorage.getItem("configs") as string);
			this.libraryDefault = servicesConfiguration.services[0].configValues[0].entryValue;
			if (
				this.input.skipDuplicateCheckEnabled &&
				servicesConfiguration.services[1].configValues.find((x: { entryKey: string; entryValue: string }) =>
					x.entryKey === 'IngestService.checkDuplicateSourceFileHash'
				)?.entryValue === 'false'
			) {
				this.input.skipDuplicateCheckEnabled = false;
			}
		}
		this.input.library = this.libraryDefault;
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.dateFormat = this.userService.getDateFormat();
		this.userPrefsUpdateHandle();
		this.loadBPResources();


		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === eventId) {
				this.userPrefsUpdateHandle();
			}
		});

		this.uploadServiceSubscription = this.uploadService.broadcastEvent.subscribe((event: any) => {
			if ("fileStatus" === event.type) {
				let file: ExtendedFile | undefined = this.uploadFiles.find(f => f.name === event.data.file.name);
				if (!file) {
					console.warn("File not found:", event.data.file.name);
					return; // Prevents errors if file is undefined
				}
				if (event.data.ready === "SUCCESS") {
					file.status = 'SUCCESS';
				} else if (event.data.ready === "FAIL" || event.data.ready === "FAILLICENSE") {
					file.status = event.data.ready;
					if (event.data.message) {
						file.message = event.data.message;
					}
					if (event.data.data !== undefined) {
						file.data = event.data.data;
					}
					if (event.data.successFiles !== undefined) {
						file.successFiles = event.data.successFiles;
					}
					if (event.data.failedFiles !== undefined) {
						file.failedFiles = event.data.failedFiles;
						if (event.data.failedFiles.length > 0) {
							let errorFile: any | undefined = event.data.failedFiles.find((f: any) => f.name === event.data.file.name);
							if (errorFile) {
								file.errorMessage = errorFile.message;
							}
						}
					}
				} else {
					file.status = 'PENDING';
				}
			} else if ("ingestStatus" === event.type) {
				switch (event.data.status) {
					case 'success':
						this.ingestMsgType = "success";
						break;
					case 'warn':
						this.ingestMsgType = "warning";
						break;
					case 'fail':
						this.ingestMsgType = "danger";
						break;
					case 'info':
						this.ingestMsgType = "info";
						break;
					default:
						this.ingestMsgType = "info";
						break;
				}
			} else if ("uploadFinished" === event.type) {
				this.resetMetadata();
				this.uploading = false;
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe((event: any) => {
			if (event.type === 'searchEnded') {
				console.log('Search Ended event received!');

				// NOTE this only contains the current paged result set, not the total found
				this.autoFiller.candidates = event.data && event.data.records ? event.data.records : [];
				this.autoFiller.index = 0;
				this.autoFiller.browsedata = [];
				this.fillFormValues();
			}
		});
	}

	ngOnDestroy(): void {
		// Clean up subscriptions
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
		if (this.uploadServiceSubscription) {
			this.uploadServiceSubscription.unsubscribe();
		}
		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}
	}

	resetFiles(): void {
		//this.logFormValidity();  
		this.uploadFiles = [];
		this.ingestMsg = "";
		this.ingestMsgType = "info";
	}

	resetMetadata(): void {
		this.formData = {
			autoDate: true,
			uploadDate: "",
			uploadName: "",
			keywords: [{ keyword: "" }],
			description: ""
		};
		this.loadSchemaFields();
		this.ingestMsg = "";
		this.ingestMsgType = "info";
	}

	addKeyword(key: string): void {
		this.formData.keywords?.push({ keyword: "" });
	}

	removeKeyword(key: number): void {
		this.formData.keywords?.splice(key, 1);
	}

	unprocessedFilter(file: File): boolean {
		return !(file as any).ready;
	}

	addFile(file: File): void {
		if (file) {
			let sizeStr = this.uploadService.formatBytes(file.size, 2);
			this.uploadService.retrievePreview(file).then((result: string) => {
				const extendedFile: ExtendedFile = Object.assign(file, {
					sizeStr: sizeStr,
					currentFileSrc: result
				});
				this.uploadFiles.push(extendedFile);
			}).catch(() => {
				const extendedFile: ExtendedFile = Object.assign(file, {
					sizeStr: sizeStr,
					currentFileSrc: "/app/aw-ui-angular/assets/img/noimage.webp"
				});
				this.uploadFiles.push(extendedFile);
			});
		};
	}

	removeFile(fileIndex: number): void {
		this.uploadFiles.splice(fileIndex, 1);
		this.ingestMsg = "";
		this.ingestMsgType = "info";
	}

	updateThumbnail(fileList: File[], index: number) {
		if (fileList.length > 0) {
			let file = fileList[0] as any; // Ideally, use a proper interface
			file.sizeStr = this.uploadService.formatBytes(file.size, 2);

			this.uploadService.retrievePreview(file).then((result: string) => {
				if (this.uploadFiles && this.uploadFiles[index]) {
					this.uploadFiles[index].currentFileSrc = result;
					this.uploadFiles[index].thumbnail = file;
					this.uploadFiles[index].thumbnail.currentFileSrc = result;
				}
			}).catch(() => {
				this.alertsService.error({
					message: this.LOCALIZATION.addThumbnailFailed,
					delay: 5000
				});
			});
		}
	}

	private getDateInputValue(classid: string): string {
		let target = document.querySelector(`.${classid}`) as HTMLInputElement;
		if (target && (target as any)._flatpickr) {
			let [datesRange] = (target as any)._flatpickr.selectedDates;
			return datesRange;
		}
		return '';
	}

	upload(): void {
		// Validate first
		const addWorkflow = this.input.uploadformat === 'individual' ? "DEFAULT" : "PACKAGE";
		const files = this.uploadFiles.map(x => x.name);
		const isConvertToPDF = this.input.runOCR;

		this.uploadService.validateIngest(files, addWorkflow, isConvertToPDF).then((data: any[]) => {
			const valid = data.every(x => x.valid);

			if (valid) {
				this.uploading = true;
				if (this.input.uploadformat === 'individual') {
					this.single();
				} else {
					this.package();
				}
			} else {
				this.alertsService.error({
					message: "Some files failed validation. Please review each error message and fix accordingly before attempting the upload again.",
					delay: 10000
				});

				this.uploadFiles.forEach(file => {
					const fileData = data.find(d => d.name === file.name && !d.valid);
					if (fileData && fileData.errorMessage) {
						file.errorMessage = fileData.errorMessage.join("\n");
					}
				});
			}
		}).catch((err: any) => {
			console.error(err);
		});
	}

	cancel(): void {
		this.uploadService.setBroadcastEvent("cancelupload", {});
		this.ingestMsgType = "warn";
		this.ingestMsg = "Uploading process cancelled.";
	}

	package(): void {
		if (!this.uploadFiles || this.uploadFiles.length < 1) {
			return;
		}

		let metadata: any = {};
		metadata.customMetadata = {};
		metadata.library = this.input.library;
		metadata.runOCR = this.input.runOCR;
		metadata.skipDuplicateCheck = this.input.skipDuplicateCheck;
		metadata.isSkipHeaderMetadtaExtraction = !this.input.extractMetadata;
		metadata.addWorkflow = this.input.addWorkflow;
		metadata.detectWorkflow = this.input.detectWorkflow;
		metadata.generatePDF = this.input.generatePDF;

		if (this.input.attachMetadata) {
			this.processCustomMetadata(metadata);
		}

		this.alertsService.info({
			title: "Upload Process Started",
			message: "Please wait for process to finish. Do not leave the page or close the window.",
			delay: 5000
		});

		this.ingestMsgType = "info";
		this.ingestMsg = "Preparing files...";
		this.scrollToMsg();

		this.uploadService.upload(this.uploadFiles, metadata);
	}

	single(): void {
		// Skip files we've already processed
		this.uploadFiles = this.uploadFiles.filter(f => !f.ready);

		if (this.uploadFiles.length < 1) {
			this.uploading = false;
			return;
		}

		let metadata: any = {
			customMetadata: {},
			library: this.input.library,
			runOCR: this.input.runOCR,
			skipDuplicateCheck: this.input.skipDuplicateCheck,
			isSkipHeaderMetadtaExtraction: !this.input.extractMetadata,
			addWorkflow: this.input.addWorkflow,
			detectWorkflow: this.input.detectWorkflow,
			generatePDF: this.input.generatePDF
		};

		if (this.input.attachMetadata) {
			this.processCustomMetadata(metadata);

			if (this.isHandleNotifications()) {
				// Add notification fields
				const emailNotification = (document.getElementById('email_notification') as HTMLInputElement)?.value;
				const printerNotification = (document.getElementById('printer_notification') as HTMLInputElement)?.value;

				if (emailNotification && emailNotification.length > 0) {
					metadata.customMetadata['email_notification'] = [emailNotification];
				}
				if (printerNotification && printerNotification.length > 0) {
					metadata.customMetadata['printer_notification'] = [printerNotification];
				}
			}
		}

		if (this.enabled.workflow) {
			// Workflow metadata fields
			metadata.customMetadata['field2'] = this.input.dxWorkflow ? [this.input.dxWorkflow.name] : [];
			metadata.customMetadata['field1'] = [this.userService.user.username];
		}

		if (this.enabled.recMgmt) {
			metadata.rmCategory = this.templateMetadata["rm_category"][0];
			metadata.rmSubcategory = this.templateMetadata["rm_subcategory"][0];
			metadata.rmFolder = "";

			if (this.input.rmFields.rm_folder_select) {
				let selectedRMFolder = this.rm_folders.find(f => f.id == this.input.rmFields.rm_folder_select);
				metadata.rmFolder = selectedRMFolder ? selectedRMFolder.name : "";
			} else {
				metadata.rmFolder = this.input.rmFields.rm_folder_input;
			}
		}

		this.alertsService.info({
			title: "Upload Process Started",
			message: "Please wait for process to finish. Do not leave the page or close the window.",
			delay: 5000
		});

		this.ingestMsgType = "info";
		this.ingestMsg = "Uploading files...";
		this.scrollToMsg();

		this.uploadService.ingest(this.uploadFiles, metadata).then(() => {
			this.uploading = false;
			let successfulUploads = this.uploadFiles.filter(f => f.status === "SUCCESS");

			if (successfulUploads.length === 0) {
				let licenseFail = this.uploadFiles.filter(f => f.status === "FAILLICENSE");

				this.ingestMsgType = "danger";
				if (licenseFail.length > 0) {
					this.ingestMsg = "Failed to upload files. License validation error.";
				} else {
					let message = '';
					let messageNumber = 1;

					this.uploadFiles.forEach(file => {
						if (file.status === 'FAIL' && file.message && file.message.length > 0) {
							message += `${messageNumber}. ${file.name}\n${file.message}\n`;
							messageNumber++;
						}
						file.click = false;
					});

					this.ingestMsg = `File upload encountered an error.\n${message.trim()}`;
				}
			} else if (successfulUploads.length < this.uploadFiles.length) {
				this.ingestMsgType = "warning";
				let message = '';
				let messageNumber = 1;

				this.uploadFiles.forEach(file => {
					if (file.status === 'FAIL' && file.message && file.message.length > 0) {
						message += `\n${messageNumber}. ${file.message}`;
						messageNumber++;
					}
					file.click = false;
				});

				this.ingestMsg = `Some files failed to upload.${message}`;
			} else {
				this.ingestMsgType = "success";
				this.ingestMsg = "Files uploaded successfully.";
			}

			this.uploadFiles = this.uploadFiles.filter(f => !f.ready);
		});
	}
	
	private scrollToMsg(): void {
		const dropzone = document.querySelector('.dropzone');
		if (dropzone && this.isOutOfView(dropzone as HTMLElement) && !this.isMobile) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}

	private isOutOfView(el: HTMLElement): boolean {
		const rect = el.getBoundingClientRect();
		const viewHeight = window.innerHeight || document.documentElement.clientHeight;
		return rect.bottom < 0 || rect.top > viewHeight;
	}

	isHandleNotifications(): boolean {
		return !!(this.configService.getConfig().upload.notificationField && this.configService.getConfig().upload.notificationField.trim().length > 0)
	}

	private processCustomMetadata(metadata: any): void {
		if (this.input.attachformat === 'template') {
			for (const property in this.templateMetadata) {
				if (
					this.templateMetadata[property] && 
					this.templateMetadata[property][0] &&
					Array.isArray(this.templateMetadata[property][0])
				) {
					// Special case when using a <select> with multiple values
					metadata.customMetadata[property] = this.templateMetadata[property][0];
				} else {
					if (this.datePickerOptions[property]) {
						// Attach calendar input values as date objects
						let values: any[] = [];
						for (let idx = 0; idx < this.templateMetadata[property].length; idx++) {
							let id = `template_${property}_${idx}`;
							values.push(this.getDateInputValue(id));
						}
						metadata.customMetadata[property] = values;
					} else {
						if (this.templateMetadata[property]){
							metadata.customMetadata[property] = this.templateMetadata[property];
						} else {
							metadata.customMetadata[property] = [''];
						}
					}
				}
			}
		} else {
			for (let i = 0; i < this.appendFields.length; i++) {
				let value = this.appendFields[i].content;
				// Attach calendar input values as date objects
				if (this.appendFields[i].type === 'date') {
					let id = `append_${this.appendFields[i].name}_${i}_0`;
					value = [this.getDateInputValue(id)];
				}
				metadata.customMetadata[this.appendFields[i].name] = value;
			}
		}
	}

	addReplacementField(index: number) {
		this.fields[index]?.replacements?.push({
			search: "",
			replacement: ""
		});
		this.updateAppendField('', this.appendFields.length - 1);
	}

	removeReplacementField(fieldIndex: number, replacementIndex: number) {
		this.fields[fieldIndex]?.replacements?.splice(replacementIndex, 1);
	}

	addAppendField() {
		if (this.formFields?.length > 0) {
			this.appendFields.push({
				name: "",
				content: [""]
			});
			this.updateAppendField('', this.appendFields.length - 1);
		}
	}

	removeAppendField(index: number) {
		this.appendFields.splice(index, 1);
	}

	addAppendValue(fieldKey: number) {
		this.appendFields[fieldKey]?.content?.push("");
	}

	removeAppendValue(fieldKey: number, index: number) {
		this.appendFields[fieldKey]?.content?.splice(index, 1);
	}

	appendTemplateField(fieldName: string) {
		this.templateMetadata[fieldName]?.push("");
	}

	removeTemplateField(fieldName: string, index: number) {
		this.templateMetadata[fieldName].splice(index, 1);
	}

	loadSchemaFields(): void {
		this.getDefaultDatePickerOptions();
		const lib = this.input.library;
		this.autoFiller = this.uploadService.getAutoFillForLibrary(lib);
		this.ingestMsg = "";
		this.ingestMsgType = "info";

		this.productService.getMetadataForm(lib).then((results: any) => {

			// Add special RM fields
			results.fields.push(
				{ name: 'rm_category', label: 'Category', show: 'always', readOnly: false, required: false, placeholder: '', isRMfield: true },
				{ name: 'rm_subcategory', label: 'Sub Category', show: 'always', readOnly: false, required: false, placeholder: '', isRMfield: true }
			);


			this.templateMetadata = {};
			results.fields.forEach((field: any) => {
				this.templateMetadata[field.name] = [""];
			});

			this.formFields = [];

			// Fetch metadata schema
			this.productService.getMetadataSchema(lib).then((schema: any) => {
				const controlledFields = (typeof schema === 'object') ? schema.properties : JSON.parse(schema).properties;

				const searchInputs = this.userService.user.searchInputs || [];
				let clonedSearchInputs = cloneDeep(searchInputs);


				for (let i = 0; i < results.fields.length; i++) {
					let formItem = results.fields[i];

					let schemaObj = controlledFields[formItem.name];
					if (!schemaObj) continue;

					let userFormField = clonedSearchInputs.find((x: any) => { return x.name === formItem.name || x.form_name === formItem.name });
					if (userFormField) {
						userFormField.name = userFormField.form_name || userFormField.name;

						this.formFields.push(userFormField);

						if (Object.keys(userFormField.dependents).length > 0) {
							userFormField.isdependent = true;
						}
					}
					else {
						console.log(`Form field ${formItem.name} is not in user's profile!`);
					}

					// Decorate the fields for form usage
					if (userFormField) {
						userFormField.hidden = false;
						userFormField.min = formItem.min;
						userFormField.max = formItem.max;
						userFormField.readOnly = formItem.readOnly;
						userFormField.required = formItem.required;
						userFormField.show = formItem.show;
						userFormField.defaultField = formItem.defaultField;

						if (schemaObj.items) {
							userFormField.ismultiple = true;
						}

						// Normalize the form field type
						userFormField.type = userFormField.type_normalized || "STRING";
						if ("INTEGER" === userFormField.type_normalized || "FLOAT" === userFormField.type_normalized || "DECIMAL" === userFormField.type_normalized || "LONG" === userFormField.type_normalized) {
							userFormField.type = "NUMBER";
						}
						else if ("LIST_MULTI" === userFormField.type_normalized || "RADIO" === userFormField.type_normalized) {
							userFormField.type = "LIST";
						}

						if (userFormField.type_normalized === 'DATE') {
							// Create field-specific datePickerOptions with min/max date
							this.datePickerOptions[userFormField.name] = this.userService.getDatePickerOptions(userFormField.min, userFormField.max, undefined, userFormField.name);
						}

						this.templateMetadata[userFormField.name] = userFormField.defaultField?.length > 0
							? [userFormField.defaultField]
							: (this.templateMetadata[userFormField.name] ? this.templateMetadata[userFormField.name] : ['']);
					}
				}

				this.onChangeRMCategory();

				this.appendFields = [{ name: this.formFields[0]?.name || '' }];
				this.updateAppendField('', 0);
			}).catch(() => console.error("Failed to get the library metadata schema."));
		}).catch(() => console.error("Failed to get the library metadata form."));
	}

	searchAutoFillCandidate(value: string, field: string) {
		if (value && value.trim() != "") {
			value = value.trim();

			console.log("Do Search - Finding auto-fill candidates for value=%s, field=%s", value, field);

			let solrQuery = `${field}:${value}`;
			let solrQueryType = "2";
			let options = {
				query: solrQuery,
				queryType: solrQueryType,
				sortField: "Rank",
				sortDirection: "DESC"
			};
			let criteria = [] as any[];

			console.log("Searching using options:");
			console.log(options);

			this.searchService.broadcastUserTriggeredCriteriaUpdateEvent(criteria, options);
		}
	}

	fillFormValues(): void {
		if (this.autoFiller.candidates.length === 0) {
			return;
		}
		const prodids: string[] = [this.autoFiller.candidates[this.autoFiller.index]["id"]];
		const keyProdids = JSON.stringify(prodids);
		const doFill = () => {
			Object.keys(this.templateMetadata).forEach((key) => {
				const foundKey = this.formFields.find((formField) => formField.name === key);
				if (foundKey && foundKey.readOnly) {
					return;
				}

				if (key === "browsepath" && !this.autoFiller.ignorable.includes(key)) {
					let idx = 0;
					if (this.autoFiller.browsedata[keyProdids]) {
						for (const path of this.autoFiller.browsedata[keyProdids]) {
							this.templateMetadata[key][idx++] = path;
						}
					}
					return;
				}

				if (this.autoFiller.candidates[this.autoFiller.index][key] &&
					!this.autoFiller.ignorable.includes(key)) {
					this.templateMetadata[key][0] = this.autoFiller.candidates[this.autoFiller.index][key];
				}
			});
		};

		if (this.autoFiller.browsedata[keyProdids]) {
			doFill();
		}

		this.browseService.getBrowsePathsForProducts(prodids)
			.then((result: Record<string, string[]>) => {
				this.autoFiller.browsedata[keyProdids] = [];
				Object.entries(result).forEach(([path, ids]) => {
					this.autoFiller.browsedata[keyProdids].push(path);
				});
			})
			.catch((error: Error | null) => {
				console.error("Error fetching browse paths for product.", error);
			})
			.finally(() => {
				doFill();
			});
	}

	openDatePicker(field: string, optsId: string): void {
		const target = document.querySelector(`#daterange_${field}`) as HTMLInputElement;
		if (!target) return;
		if (target && !(target as any)._flatpickr) {
			flatpickr(target, this.datePickerOptions[optsId]);
		}
		if (target && (target as any)._flatpickr.isOpen) {
			(target as any)._flatpickr.close();
		} else {
			(target as any)._flatpickr.open();
		}
	}

	getDefaultDatePickerOptions(): void {
		// This scope object is a map of options for each date field:
		// {"fieldName": {theOptions} }
		this.datePickerOptions = {};
	}

	onChangeRMCategory(): void {
		if (!this.enabled?.recMgmt) { return; }
		this.rm_folders = [];
		let cid = Array.isArray(this.templateMetadata["rm_category"])
			? this.templateMetadata["rm_category"][0] ?? null
			: null;
		let scid = Array.isArray(this.templateMetadata["rm_category"])
			? this.templateMetadata["rm_subcategory"][0] ?? null
			: null;
		let returnall = false;
		if (!cid) { returnall = true; }
		this.filingService.getFoldersForCategory(cid, scid, true, returnall).then(
			(response: any) => {
				this.rm_folders = response;
			},
			(err: any) => {
				console.error("Could not retrieve available folders.", err);
			}
		);
	}

	updateAppendField(selectedValue: string, key: number) {
		let a = this.formFields.find(
			(x) => x.name === selectedValue
		);
		if (a) {
			this.appendFields[key] = {
				...this.appendFields[key], // Preserve existing properties
				content: [""],
				type: a.type,
				allowedValues: a.allowedValues,
				placeholder: a.placeholder,
				required: a.required,
				min: a.min,
				max: a.max
			};
			if (selectedValue.length > 0) {
				this.appendFields[key].name = selectedValue;
			}
		} else {
			this.appendFields[key] = {
				...this.appendFields[key], // Preserve existing properties
				content: [""],
				type: "STRING",
				placeholder: "",
				required: false
			};
		}
	}

	private userPrefsUpdateHandle(): void {
		this.user = this.userService.user;

		let permissions: { [key: string]: number } = {};
		if (this.user?.permissions) {
			permissions = this.user.permissions.reduce((map: any, obj: string) => {
				map[obj] = 1;
				return map;
			}, {});
		}

		this.uploadOn = !!permissions['ROLE_PRODUCT_ADD'];
		this.enabled.rmEnabled = !!permissions['ROLE_RECORDS_MANAGEMENT_EDIT'];

		this.libraries = this.user?.properties?.awlibraryObjectList || [];

		if (this.user?.properties) {
			this.libraries = this.libraries.filter(v => v.accessLevel > 2);
		}

		if (this.libraries.length > 0) {
			this.input.library = this.libraryDefault;
			this.loadSchemaFields();
			this.loadWorkflows();
		} else {
			this.uploadOn = false;
		}
	}

	loadWorkflows() {
		this.addWorkflows = [];
		this.detectWorkflows = [];
		this.productService.addWorkflowList().then((data: string[]) => this.addWorkflows = data)
			.catch(() => this.addWorkflows = []);
		this.productService.detectWorkflowList().then((data: string[]) => this.detectWorkflows = data)
			.catch(() => this.detectWorkflows = []);
	}

	prevFillCandidate() {
		if (this.autoFiller?.index > 0) {
			this.fillFormValues();
		}
	}

	nextFillCandidate() {
		if (this.autoFiller?.index < this.autoFiller?.candidates?.length - 1) {
			++this.autoFiller.index;
			this.fillFormValues();
		}
	}

	updateFn(formItem: any, formValue: any) {
		this.setNotificationFields(formItem);

		// This function is implemented in fields.service.ts
		formItem.updateFn(formItem, false, formValue);
	}

	setNotificationFields(formItem: any): void {
		if (
			this.isHandleNotifications() &&
			this.input.attachMetadata &&
			formItem &&
			formItem.name === this.configService.getConfig().upload.notificationField
		) {
			// Populate metadata
			let metadata: any = {
				customMetadata: {},
				library: this.input.library,
				runOCR: this.input.runOCR,
				skipDuplicateCheck: this.input.skipDuplicateCheck,
				isSkipHeaderMetadtaExtraction: !this.input.extractMetadata,
				addWorkflow: this.input.addWorkflow,
				detectWorkflow: this.input.detectWorkflow,
				generatePDF: this.input.generatePDF
			};

			this.processCustomMetadata(metadata);

			// Fetch notification fields
			this.uploadService.getNotificationFields(metadata).then((results: any) => {
				if (results) {
					let fields = JSON.parse(results);
					this.emailNotification = fields.email_notification || '';
					this.printerNotification = fields.printer_notification || '';
				}
			}
			).catch(() => console.error('Error fetching notification fields:'));
		}
	}

	loadBPResources() {
		this.dxUtilsService.getEngineReadyState().then((result: any) => {
			if (this.dxUtilsService.enabled) {
				this.dxBatchService.getWorkflows().then((result: any) => {
					this.workflows = result;
				}).catch(() => console.error('Could not retrieve available workflows'));
			}
		}).catch(() => console.error('BP is not ready'));
	}

	/** TODO revisit, what type of  field: string?*/

	handleDateEnter(type: string, field: string, index: number) {
		if (type == 'template') {
			if (this.templateMetadata[field]) {
				this.templateMetadata[field][index] = dayjs();
			}
		} else {
			const num = Number(field);
			if (!isNaN(num) && this.appendFields[num]) {
				this.appendFields[num].content[index] = dayjs();
			}
		}
	}

	trackByConfigId(index: number, workflow: any): string {
		return workflow.config.id;
	}

	getFilteredOptions(field: any): any[] {
		if (!field.allowedValues) return [];
		if (!field.filter) return field.allowedValues;

		return field.allowedValues.filter((option: any) =>
			option.name.toLowerCase().includes(field.filter.toLowerCase())
		);
	}

	getUnprocessedFilteredFiles() {
		return this.uploadFiles.filter(item => !item.ready);
	}

	onFilesSelected(files: File[]) {
		files.forEach(file => {
			console.log('Added file:', file);
			this.addFile(file);
		});
	}

	onDrop(event: DragEvent): void {
		event.stopPropagation();
		event.preventDefault();

		if (event.dataTransfer?.files) {
			console.log('Dropped files:', event.dataTransfer.files);

			const files: File[] = Array.from(event.dataTransfer.files); // Convert FileList to Array

			// Use NgZone to update Angular's change detection
			this.ngZone.run(() => {
				files.forEach(file => this.addFile(file));
			});
		}
	}

	onDragOver(event: Event): void {
		event.stopPropagation();
		event.preventDefault(); // Prevents default behavior to allow dropping
	}

	onThumbnailSelected(event: Event, i: number) {
		const target = event.target as HTMLInputElement;
		if (target.files) {
			console.log('Selected files:', target.files);
			// Handle file upload logic here
			const files: File[] = Array.from(target.files);
			this.updateThumbnail(files, i);
		}
	}

	trackByFn(index: number, item: any): number {
		return index; // Or use a unique ID
	}

	getAppendFieldName(key: number): string {
		return this.appendFields[key]?.name || '';
	}

	getAriaLabel(key: number): string {
		return `field ${key}`;
	}

	toggleBrowsepathEdit(key: number, index: number): void {
		this.uploadService.broadcastEvent.emit({ type: "closeModal", data: {} });
		const browsepath = {
			lib_name: this.input.library,
			path: this.input.attachformat === 'dynamic' ? this.appendFields[key].content : this.templateMetadata[key]
		};

		const productGroup = {
			records: [],
			ids: []
		};

		const modalRef = this.modalService.open(BrowseEditComponent, {
			size: 'md',
			backdrop: false,
			centered: true
		});

		// Pass data to the modal
		modalRef.componentInstance.browsepath = browsepath;
		modalRef.componentInstance.productGroup = productGroup;

		// Handle modal close
		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					if (this.input.attachformat == "dynamic") {
						this.appendFields[key].content = payload.result.caller.data;
					} else {
						this.templateMetadata[key] = payload.result.caller.data;
					}
				}
			},
			(reason) => {
				console.log('Modal dismissed:', reason);
			}
		);
	}

	toggleEmailsEdit(field: string): void {
		const modalRef = this.modalService.open(EmailModalComponent, {
			size: 'md',
			backdrop: false,
			centered: true,
			container: 'body'
		});

		// Pass data to the modal
		//modalRef.componentInstance.browsepath = browsepath;

		// Handle modal close
		modalRef.result.then(
			(result: string) => {
				const emailNotification = (document.getElementById('email_notification') as HTMLInputElement);
				if (emailNotification) {
					(document.getElementById('email_notification') as HTMLInputElement).value = result;
				}
			},
			(reason) => {
				console.log('Modal dismissed:', reason);
			}
		);
	}

	private logFormValidity() {
		console.log(this.kvForm.valid);
	}
}
